'use client';

import { I18n } from '@shopify/polaris/build/ts/src/utilities/i18n';
import en from '@shopify/polaris/locales/en.json';
import { SettingKey } from 'data/constants-skeleton';
import i18next from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useAppSelector } from 'redux/hooks';
import { AppLanguage } from 'services/LanguageService';
import { POLARIS_LANGUAGES, TranslationDictionary } from '../polaris-translation/polaris-languages';

type LanguageStrings = ConstructorParameters<typeof I18n>[0];

export { POLARIS_LANGUAGES };

/**
 * If the User has the default language (English) selected in PickupBird, and Shopify language is present, but not English, then sets i18next to, and returns the Shopify language preference.
 *
 * Otherwise, sets i18next to, and returns the PickupBird language preference.
 *
 * @returns {TranslationDictionary[]} A Shopify Polaris i18n object, or array, to be passed along to the AppProvider. Always contains English as a fallback.
 *  */
export default function usePolarisLanguage(lang?: string): TranslationDictionary | TranslationDictionary[] {
    const [polarisTranslations, setPolarisTranslations] = useState<LanguageStrings>([en]);
    const [shopifyLanguage, setShopifyLanguage] = useState<AppLanguage>(undefined);

    const [params] = useSearchParams();
    const settings = useAppSelector((state) => state.settings);
    const pickupBirdLanguage: string = lang ?? AppLanguage[settings.settings?.[SettingKey.default_language]];

    useEffect(() => {
        if (params?.has('locale')) {
            const shopifyLanguageFromParam = params?.get('locale')?.split('-')[0] as AppLanguage;
            if (
                shopifyLanguageFromParam &&
                (!shopifyLanguage || (shopifyLanguage && shopifyLanguage !== shopifyLanguageFromParam))
            ) {
                setShopifyLanguage(shopifyLanguageFromParam);
            }
        }
    }, [params, shopifyLanguage]);

    const language: AppLanguage = useMemo((): AppLanguage => {
        if (!!pickupBirdLanguage && pickupBirdLanguage === 'en' && !!shopifyLanguage && shopifyLanguage !== 'en') {
            return shopifyLanguage as AppLanguage;
        } else if (!pickupBirdLanguage && !!shopifyLanguage) {
            return shopifyLanguage as AppLanguage;
        }
        return pickupBirdLanguage as AppLanguage;
    }, [pickupBirdLanguage, shopifyLanguage]);

    useEffect(() => {
        i18next.changeLanguage(language);
        setPolarisTranslations([POLARIS_LANGUAGES[language], en]);
    }, [language]);

    return polarisTranslations;
}
